import { Component, ChangeDetectionStrategy, forwardRef, Output, EventEmitter } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { PrimerComponent } from '../primer.component';
import { IHeadlessPaymentMethodButton, Primer } from '@primer-io/checkout-web';
import { OrderformRedirectResponse } from 'libs/orderform-widgets/src/lib/ngrx/orderform.models';
import { PrimerPaymentMethodType } from '../primer.model';
import { IconColor, IconName } from '@ds24/elements';
import { PrimerConfirmModalComponent } from '../primer-confirm-modal/primer-confirm-modal.component';

@Component({
  selector: 'orderform-payment-method-primer-google-apple-pay',
  templateUrl: './primer-ga.component.html',
  styleUrls: ['./primer-ga.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PrimerGoogleApplePayComponent),
      multi: true,
    },
  ],
})
export class PrimerGoogleApplePayComponent extends PrimerComponent {
  private _GAPayButton: IHeadlessPaymentMethodButton;
  private _paymentMethodManager: any;
  @Output() readonly paymentMethodTypeDetected = new EventEmitter<PrimerPaymentMethodType>();
  paymentMethodType: PrimerPaymentMethodType = PrimerPaymentMethodType.NONE;
  protected _googleApplePayButtonId = 'GoogleApplePay-Button';
  protected readonly IconName = IconName;
  protected readonly IconColor = IconColor;
  protected _modalInstance: PrimerConfirmModalComponent | null = null;

  async initializePrimerHeadless() {
    try {
      const order = await this.getOrder();
      const clientSession = await this.createClientSession(order);
      this.clientToken = clientSession.clientToken;
      if (!clientSession || !this.clientToken) {
        this._toaster.error('Payment provider is currently unavailable. Please try another payment method.');
        return;
      }

      this._headless = await Primer.createHeadless(this.clientToken, {
        onAvailablePaymentMethodsLoad: async (paymentMethods) => {
          const googlePayMethod = paymentMethods.find(
            (method) => method.managerType === 'NATIVE' && method.type === PrimerPaymentMethodType.GOOGLE_PAY
          );

          const applePayMethod = paymentMethods.find(
            (method) => method.managerType === 'NATIVE' && method.type === PrimerPaymentMethodType.APPLE_PAY
          );

          if (applePayMethod) {
            // Prioritize Apple Pay if both are available (in order to only show one button)
            this._paymentMethodManager = await this._headless.createPaymentMethodManager(
              PrimerPaymentMethodType.APPLE_PAY
            );

            this.paymentMethodType = PrimerPaymentMethodType.APPLE_PAY;

            this.paymentMethodTypeDetected.emit(PrimerPaymentMethodType.APPLE_PAY);
          } else if (googlePayMethod) {
            // Use Google Pay if Apple Pay isn't available
            this._paymentMethodManager = await this._headless.createPaymentMethodManager(
              PrimerPaymentMethodType.GOOGLE_PAY
            );

            this.paymentMethodType = PrimerPaymentMethodType.GOOGLE_PAY;

            this.paymentMethodTypeDetected.emit(PrimerPaymentMethodType.GOOGLE_PAY);
          } else {
            // Neither is available
            this.paymentMethodType = PrimerPaymentMethodType.NONE;
            this.paymentMethodTypeDetected.emit(PrimerPaymentMethodType.NONE);
          }
        },
        onCheckoutComplete: async (data) => {
          await this._handleCheckoutComplete(data);
        },
        onCheckoutFail: async (error, { payment }, handler) => {
          return this._handleCheckoutFail(error, payment, handler, 'ds-orderform-payment-primer-apple-google-pay');
        },
        onPaymentMethodAction: (action, { paymentMethodType }) => {
          if (action === 'PAYMENT_METHOD_SELECTED') {
            if (this._modalInstance) {
              this._modalInstance.setProcessingOrder(true);
            }
          } else if (action === 'PAYMENT_METHOD_UNSELECTED') {
            if (this._modalInstance) {
              this._modalInstance.setProcessingOrder(false);
            }
          }
        },
      });

      await this._headless.start();
    } catch (error) {
      this._hidePaymentMethodWithError(error, 'primer-apple-google-pay');
    }
  }

  async _onBeforeSubmit(): Promise<{ valid: boolean }> {
    if (!this.isActive()) {
      return Promise.resolve({ valid: true });
    }

    const refreshed = await this._headless.refreshClientSession();

    if (!refreshed || !this.clientToken) {
      this._paymentDataChanged.next({
        valid: false,
      });
      return { valid: false };
    }

    this._paymentDataChanged.next({
      valid: true,
      primer_token: this.clientToken,
    });
    return { valid: true };
  }

  async _handlePrimerRedirect(redirectData: OrderformRedirectResponse): Promise<void> {
    const shouldContinue = await this._handlePrimerRedirectCommon(redirectData);
    if (!shouldContinue) return;

    // Only these lines are specific to the Google/Apple Pay implementation
    this._openGAPayModal();
    this._numberOfSubmitCalls++;
  }

  protected getComponentName(): string {
    return 'ds-orderform-payment-primer-apple-google-pay';
  }

  protected async _openGAPayModal() {
    await this._headless.refreshClientSession();

    const dialogRef = this._dialog.open(PrimerConfirmModalComponent, {
      data: {
        paymentMethodType: this.paymentMethodType,
        buttonId: this._googleApplePayButtonId,
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this._numberOfSubmitCalls = 0;
    });

    this._GAPayButton = this._paymentMethodManager.createButton();
    this._modalInstance = dialogRef.componentInstance;
    this._modalInstance.paymentMethodType = this.paymentMethodType;
    this._modalInstance.buttonId = this._googleApplePayButtonId;
    this._modalInstance.dialogRef = dialogRef;
    this._modalInstance.paymentButton = this._GAPayButton;
  }
}
